import React from "react";
import type { GetStaticProps, NextPage } from "next";
import { SimpleGrid, Text, Box, Flex, Button } from "@chakra-ui/react";
import Image from "next/image";
import {
  type IntegrationSimple,
  type Banner,
  type Page as SanityPage,
  type Customer,
  getAnnouncementBanner,
  getPage,
  getSimpleIntegrations,
  getStackedLogos,
} from "lib/sanity";
import { Page } from "components/page";
import { Section } from "components/section";
import { HalfHeroAnimation } from "components/half-hero-animation";
import UseCaseAnimation, { type UseCaseProps } from "components/use-case-animation";
import { StackedLogos } from "components/logos";
import ActivateDataSteps from "components/activate-data-steps";
import { Quote } from "components/quotes";
import { DemoEmbedHalf } from "components/demo-embed";
import { Feature } from "components/feature";
import DestinationMarquee from "components/destination-marquee";
import { SecurityFeatures } from "components/content";
import { getPageMetaImage } from "utils";
import { SectionHeading } from "components/section-heading";
import { Link } from "ui/link";
import { PairImage } from "components/pair-image";
import { integrationsCount } from "../../constants/integrations";
import fullWidthImage from "../../../public/assets/platform/reverse-etl/full-width.png";
import fullWidthMobileImage from "../../../public/assets/platform/reverse-etl/full-width-mobile.png";
import pair1Image from "../../../public/assets/platform/reverse-etl/pair-1.png";
import pair2Image from "../../../public/assets/platform/reverse-etl/pair-2.png";
import pair3Image from "../../../public/assets/platform/reverse-etl/pair-3.png";
import pair4Image from "../../../public/assets/platform/reverse-etl/pair-4.png";
import pair5Image from "../../../public/assets/platform/reverse-etl/pair-5.png";

type PageProps = {
  page: SanityPage;
  destinations: IntegrationSimple[];
  announcementBanner?: Banner;
  useCases: UseCaseProps[];
  customerRows: Customer[][];
};

const ReverseETL: NextPage<Readonly<PageProps>> = ({
  page,
  destinations,
  announcementBanner,
  useCases,
  customerRows,
}) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHeroAnimation
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        animationElement={<UseCaseAnimation isHero useCases={useCases} />}
      />

      <StackedLogos
        customerRows={customerRows}
        title="#1 in Reverse ETL and trusted by industry leading data teams"
        titleMaxW="100%"
      />

      <Section center bg="infographicGradient" color="white">
        <Box textAlign="center">
          <Text as="h2" textStyle="h4">
            Reverse ETL: <br /> the last mile of your data stack
          </Text>

          <Text fontSize="lg" mt={5} maxW="2xl" mx="auto">
            Hightouch works with the tools and workflows you already have. Go beyond analytics to extend the reach of
            your warehouse directly into over {integrationsCount} SaaS applications. No custom work required.
          </Text>
        </Box>

        <Box display={{ base: "none", md: "block" }} pos="relative" w="100%" mt={{ base: 12, md: 16, lg: 20 }}>
          <Image
            src={fullWidthImage}
            alt="Diagram of data ingestion, data modeling / analysis, and data activation with Hightouch."
            width={1200}
            height={430}
          />
        </Box>

        <Flex
          display={{ base: "flex", md: "none" }}
          pos="relative"
          align="center"
          justify="center"
          w="70%"
          maxW="300px"
          mx="auto"
          mt={{ base: 16, md: 24 }}
        >
          <Image
            src={fullWidthMobileImage}
            alt="Diagram of data ingestion, data modeling / analysis, and data activation with Hightouch."
            width={264}
            height={1326}
          />
        </Flex>
      </Section>

      <ActivateDataSteps />

      <Section>
        <Quote
          quote="Data is now accessible to every stakeholder within the company regardless of their technical abilities."
          author="Remi Paulin"
          authorImage="https://cdn.sanity.io/images/pwmfmi47/production/3542c6de84ba7c9659e92508c5f606ce3faa4272-450x450.png"
          job="Data Architect"
          company="PrestaShop"
          logo="https://cdn.sanity.io/images/pwmfmi47/production/b06627e2046dc9f06eb617aed6acf1ff7c5bd1f7-445x369.png"
          href="/customers/prestashop"
        />
      </Section>

      <DemoEmbedHalf
        title="See how it works in real life"
        description={
          <Text>
            Check out this 5 minute interactive demo to learn how to sync data from your data warehouse to any SaaS
            tool.
          </Text>
        }
      />

      <Section>
        <SimpleGrid columns={{ sm: 1, md: 2, lg: 4 }} gap={16} mt={{ base: 8, md: 16 }}>
          <Feature
            image="/assets/platform/reverse-etl/feature-1.svg"
            imageWidth="54px"
            title="Built to scale"
            description="Hightouch queries hundreds of billions of rows every month."
          />

          <Feature
            image="/assets/platform/reverse-etl/feature-2.svg"
            imageWidth="54px"
            title="Fast and observable"
            description="90% of syncs complete in 30 seconds or less."
          />

          <Feature
            image="/assets/platform/reverse-etl/feature-3.svg"
            imageWidth="54px"
            title="Secure by default"
            description="Hightouch never stores your data."
          />

          <Feature
            image="/assets/platform/reverse-etl/feature-4.svg"
            imageWidth="54px"
            title="Under your control"
            description="Maintain full authority with SSO, granular permissions, and access controls."
          />
        </SimpleGrid>
      </Section>

      <Section>
        <PairImage
          align="right"
          image={pair1Image}
          title="dbt compatible for models and lineage"
          description={
            <Text>
              Hightouch’s dbt model selector allows you to pull your existing dbt models via Git and send data to over{" "}
              {integrationsCount} destinations. That way, whenever you update your dbt models, Hightouch automatically
              reflects those changes in your syncs. Plus, dbt CI checks and our native dbt Exposures integration give
              you the guardrails and lineage needed to make sure your downstream Hightouch syncs don’t break.
            </Text>
          }
          actionText="Learn more about our dbt integrations"
          actionUrl="/blog/dbt-and-hightouch-are-putting-your-transformed-data-to-work"
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={pair2Image}
          title="Live debugging for any failed rows"
          description={
            <Text>
              Dive deeper into failed runs with full API requests and responses. Filter and search across the entirety
              of synced rows, without sampling, to quickly identify problematic data. Debugger data is exportable to CSV
              for adhoc analysis.
            </Text>
          }
          actionText="Learn more about our live debugger"
          actionUrl="/blog/observability-better-than-ever"
        />
      </Section>

      <Section center bg="#298B5E" color="white" pos="relative">
        <Box
          pos="absolute"
          top={0}
          bottom={0}
          left={0}
          w={{ base: "20%", md: "24%", xl: "30%" }}
          bgImage="/assets/platform/reverse-etl/stream-bg-left.png"
          bgSize="cover"
          bgRepeat="no-repeat"
          bgPos="right"
        />
        <Box
          pos="absolute"
          top={0}
          bottom={0}
          right={0}
          w={{ base: "20%", md: "24%", xl: "30%" }}
          bgImage="/assets/platform/reverse-etl/stream-bg-right.png"
          bgSize="cover"
          bgRepeat="no-repeat"
          bgPos="left"
        />
        <SectionHeading
          title="Stream data for lightning-fast activation"
          description="Sync data to your tools faster than ever before with Streaming Reverse ETL. Hightouch can now power any time-sensitive data workflow by leveraging cutting-edge warehouse features (e.g. Dynamic Tables) with the first-ever Streaming Reverse ETL platform."
          maxW={{ base: "xl", md: "2xl", lg: "3xl" }}
          mx="auto"
          zIndex={1}
        />
        <Button
          as={Link}
          href="/blog/announcing-streaming-reverse-etl"
          size={{ base: "md", md: "lg" }}
          variant="white"
          mt={{ base: 9, lg: 12 }}
        >
          Learn more
        </Button>
      </Section>

      <Section>
        <PairImage
          align="right"
          image={pair3Image}
          title="Observability and alerting"
          description={
            <Text>
              Write all of the results of your syncs back to your warehouse so you can investigate trends with the full
              flexibility of SQL. Get ahead of potential data pipeline issues with configurable alerts. Hightouch also
              integrates with tools like Datafold and Datadog for deeper monitoring across your entire data stack.
            </Text>
          }
          actionText="Learn more about observability"
          actionUrl="/platform/observability"
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={pair4Image}
          title="Version control with Git"
          description={
            <Text>
              Use GitHub or Bitbucket to bi-directionally sync all configuration changes from Hightouch or your CLI. You
              can see all edits and rollback unintended changes immediately and require PRs to ensure all changes are
              approved.
            </Text>
          }
          actionText="Learn more about version control"
          actionUrl="/blog/announcing-git-sync-back-your-syncs-with-version-control"
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={pair5Image}
          title="Environments and deployments"
          description={
            <Text>
              Create and manage custom environments for your organization. Each environment is a separate workspace,
              with its own resources and settings. Safely test your models and sync configurations before deploying them
              to production.
            </Text>
          }
          actionText="Learn more about environments"
          actionUrl="/blog/introducing-hightouch-environments"
        />
      </Section>

      <DestinationMarquee destinations={destinations} />

      <SecurityFeatures />
    </Page>
  );
};

const getSource = (sourceSlug: string, sources: IntegrationSimple[]): IntegrationSimple | undefined => {
  return sources.find(({ slug }) => slug === sourceSlug);
};

const getDestination = (destinationSlug: string, destinations: IntegrationSimple[]): IntegrationSimple | undefined => {
  return destinations.find(({ slug }) => slug === destinationSlug);
};

const getUseCases = (sources: IntegrationSimple[], destinations: IntegrationSimple[]): UseCaseProps[] => {
  return [
    {
      id: "snowflake-salesforce",
      head: {
        source: getSource("snowflake", sources),
        sourceTitle: "Users",
        destination: getDestination("salesforce", destinations),
        destinationTitle: "Contact",
      },
      rows: [
        { sourceData: "email", destinationData: "Email address", destinationIcon: "email" },
        { sourceData: "workspace", destinationData: "Company", destinationIcon: "text" },
        { sourceData: "last_sign_in", destinationData: "Last active", destinationIcon: "time" },
      ],
    },
    {
      id: "databricks-iterable",
      head: {
        source: getSource("databricks", sources),
        sourceTitle: "Users",
        destination: getDestination("iterable", destinations),
        destinationTitle: "Contact",
      },
      rows: [
        { sourceData: "email", destinationData: "Email address", destinationIcon: "email" },
        { sourceData: "name", destinationData: "Name", destinationIcon: "text" },
        { sourceData: "last_sign_in", destinationData: "Last active", destinationIcon: "time" },
      ],
    },
    {
      id: "snowflake-google",
      head: {
        source: getSource("snowflake", sources),
        sourceTitle: "Users",
        destination: getDestination("google", destinations),
        destinationTitle: "Audiences",
      },
      rows: [
        { sourceData: "email", destinationData: "Email address", destinationIcon: "email" },
        { sourceData: "phone", destinationData: "Phone number", destinationIcon: "number" },
        { sourceData: "last_name", destinationData: "Last name", destinationIcon: "text" },
      ],
    },
    {
      id: "bigquery-hubspot",
      head: {
        source: getSource("google-bigquery", sources),
        sourceTitle: "Workspaces",
        destination: getDestination("hubspot", destinations),
        destinationTitle: "Company",
      },
      rows: [
        { sourceData: "name", destinationData: "Name", destinationIcon: "text" },
        { sourceData: "Domain", destinationData: "Domain name", destinationIcon: "text" },
        { sourceData: "num_users", destinationData: "Number of users", destinationIcon: "number" },
      ],
    },
  ];
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, destinations, announcementBanner, useCaseSources, useCaseDestinations, customerRows] = await Promise.all(
    [
      getPage("/platform/reverse-etl"),
      getSimpleIntegrations("destination"),
      getAnnouncementBanner(),
      getSimpleIntegrations("source", ["snowflake", "google-bigquery", "databricks"]),
      getSimpleIntegrations("destination", ["salesforce", "hubspot", "iterable", "google"]),
      getStackedLogos(),
    ],
  );

  const useCases = getUseCases(useCaseSources, useCaseDestinations);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      announcementBanner,
      useCases,
      customerRows,
    },
    revalidate: 60,
  };
};

export default ReverseETL;
